import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { getProductTypeFromTypename } from 'bundles/collections-carousel/constants';
import type { CarouselCollection, CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';
import CareerRoleRelatedDegreeCollectionQuery from 'bundles/unified-career-academy/queries/CareerRoleRelatedDegreeCollectionQuery.graphql';
import type {
  CareerRoleRelatedDegreeCollectionFragment,
  CareerRoleRelatedDegreeCollectionQueryQueryVariables,
  CareerRoleRelatedDegreeCollectionQueryQuery as CareerRoleRelatedDegreeCollectionQueryType,
} from 'bundles/unified-career-academy/queries/__generated__/CareerRoleRelatedDegreeCollectionQuery';
import { getRelatedDegreeCollectionMetadataByRoleSlug } from 'bundles/unified-career-academy/utils/getRelatedDegreeCollectionMetadataByRoleSlug';

const QUERY_COLLECTION_ID = 'promo-template-preset-collection';

export const normalizeDegreeCollection = (
  content?: CareerRoleRelatedDegreeCollectionFragment
): CarouselCollection | null => {
  if (!content?.entities?.elements) return null;

  const normalizedEntities: CollectionEntity[] = content.entities.elements.flatMap((entity) => {
    if (entity?.__typename === 'ProductCard_ProductCard') {
      const { marketingProductType, productTypeAttributes } = entity;

      if ('canonical' in productTypeAttributes && productTypeAttributes.canonical) {
        const canonical = productTypeAttributes.canonical;
        return [
          {
            id: canonical.id,
            name: canonical.name,
            slug: canonical.slug,
            imageUrl: canonical.thumbnailUrl,
            partners: canonical.partners?.map((partner) => ({
              id: partner.id,
              name: partner.name,
              logo: partner.squareLogo,
            })),
            partnerLogos: canonical.partners?.map((partner) => partner.squareLogo || '') || [],
            link: '/degrees/' + canonical.slug,
            productType: getProductTypeFromTypename(marketingProductType),
          },
        ] as CollectionEntity[];
      }
    }

    return [];
  });

  const collection: CarouselCollection = {
    collectionId: content.id,
    title: content.description,
    entities: normalizedEntities,
  };

  return collection;
};

export const useRelatedDegrees = ({
  ssr,
  skip,
  slug,
  limit = 40, // some roles have up to 32 mapping degrees for now
}: {
  ssr: boolean;
  skip?: boolean;
  slug: string;
  limit?: number;
}) => {
  const presetCollectionId = getRelatedDegreeCollectionMetadataByRoleSlug(slug)?.collectionId;
  const skipQuery = skip || !slug || !presetCollectionId;
  const { data, loading } = useQuery<
    CareerRoleRelatedDegreeCollectionQueryType,
    CareerRoleRelatedDegreeCollectionQueryQueryVariables
  >(CareerRoleRelatedDegreeCollectionQuery, {
    variables: {
      limit,
      queryCollectionId: QUERY_COLLECTION_ID,
      additionalInput: {
        preset_collection_id: presetCollectionId,
      },
    },
    ssr,
    skip: skipQuery,
    errorPolicy: 'all' as const,
    fetchPolicy: 'no-cache', // workaround for the issue that no degree collection is returned for role "career counselor"
    context: { clientName: 'gatewayGql' },
    onError: (error) =>
      Sentry.captureException(error, {
        extra: {
          message: `Error fetching "${slug}" career role related degree collection with collection id "${presetCollectionId}"`,
        },
      }),
  });

  if (skipQuery) {
    return { loading: false, data: null };
  }

  return { data: normalizeDegreeCollection(data?.Collection?.queryCollection), loading };
};
